import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";

export const FIELDS = [["comment", "💬 Kommentare"], ["like", "❤️ Likes"], ["photo", "📸 Fotos"], ["post", "📝 Beiträge"], ["invite", "🤝 Freunde einladen"]];
const pad = (n) => String(n).padStart(2, "0");
const local = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const tonight = () => { const d = new Date(); d.setHours(23, 59, 0, 0); if (d - Date.now() < 3600000) d.setDate(d.getDate() + 1); return local(d); };
const inHours = (h) => local(new Date(Date.now() + h * 3600000));
const sundayNight = () => { const d = new Date(); d.setDate(d.getDate() + ((7 - d.getDay()) % 7)); d.setHours(23, 59, 0, 0); if (d - Date.now() < 3600000) d.setDate(d.getDate() + 7); return local(d); };
const TEMPLATES = [
  { name: "Like-Marathon", emoji: "❤️", hint: "1.000 Likes in 6 Std.", goals: { like: 1000 }, stake: 300, end: () => inHours(6) },
  { name: "Foto-Sonntag", emoji: "📸", hint: "100 Fotos bis So 23:59", goals: { photo: 100 }, stake: 500, end: sundayNight },
  { name: "Kommentar-Party", emoji: "💬", hint: "800 Kommentare bis heute Nacht", goals: { comment: 800 }, stake: 400, end: tonight },
  { name: "Freunde-Wochenende", emoji: "🤝", hint: "30 Freunde in 48 Std.", goals: { invite: 30 }, stake: 700, end: () => inHours(48) },
  { name: "Alles gemeinsam", emoji: "🔥", hint: "500/500/50/20 bis heute Nacht", goals: { comment: 500, like: 500, photo: 50, invite: 20 }, stake: 500, end: tonight },
];

function Templates({ onDone }) {
  const start = async (t) => {
    if (!window.confirm(`„${t.name}“ jetzt starten? (${t.hint}, Einsatz ±${t.stake} Punkte) Alle Mitglieder bekommen eine Push.`)) return;
    try {
      await api.post("/admin/quest/new", { title: t.name, stake: t.stake, end: t.end(), goals: t.goals });
      toast.success(`„${t.name}“ läuft 🔥`); onDone();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-2" data-testid="admin-quest-templates">
      <p className="text-sm font-bold">Vorlagen – mit einem Klick starten</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {TEMPLATES.map((t) => (
          <button key={t.name} onClick={() => start(t)} data-testid={`admin-quest-template-${t.name.toLowerCase().replace(/\s+/g, "-")}`} className="text-left rounded-2xl border border-red-100 bg-red-50/40 hover:bg-red-50 px-4 py-3 transition-colors">
            <p className="font-bold">{t.emoji} {t.name}</p>
            <p className="text-xs text-slate-500">{t.hint} · ±{t.stake} Punkte</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function QuestNewForm({ onDone }) {
  const [f, setF] = useState({ title: "Community-Aufgabe", comment: 500, like: 500, photo: 50, invite: 20, stake: 500, end: tonight() });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [ai, setAi] = useState(false);
  const suggest = async () => {
    setAi(true);
    try {
      const { data } = await api.post("/admin/ai/quest-idea");
      setF({ ...f, title: data.title, stake: data.stake, end: local(new Date(Date.now() + data.hours * 3600000)), ...data.goals });
      toast.success("Vorschlag eingefügt – prüfen und starten");
    } catch (e) { toast.error(formatErr(e)); } finally { setAi(false); }
  };
  const save = async () => {
    if (!window.confirm("Neue Community-Aufgabe starten? Alle Mitglieder bekommen eine Push.")) return;
    setBusy(true);
    try {
      await api.post("/admin/quest/new", { title: f.title, stake: Number(f.stake), end: f.end, goals: Object.fromEntries(FIELDS.map(([k]) => [k, Number(f[k]) || 0])) });
      toast.success("Neue Community-Aufgabe läuft 🔥"); onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-red-100 p-4 space-y-4" data-testid="admin-quest-new">
      <Templates onDone={onDone} />
      <p className="text-sm font-bold flex items-center gap-2"><Plus className="w-4 h-4 text-[#d2042d]" /> Oder eigene Community-Aufgabe anlegen</p>
      <button type="button" onClick={suggest} disabled={ai} data-testid="admin-quest-ai" className="h-9 px-4 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-bold flex items-center gap-1.5 disabled:opacity-60">
        {ai ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Neue Idee von ChatGPT
      </button>
      <Input value={f.title} onChange={set("title")} maxLength={60} placeholder="Titel" data-testid="admin-quest-new-title" className="rounded-xl" />
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(([k, l]) => (
          <label key={k} className="text-xs font-semibold text-slate-500">{l}<Input type="number" min={0} value={f[k]} onChange={set(k)} data-testid={`admin-quest-new-${k}`} className="rounded-xl mt-1" /></label>
        ))}
        <label className="text-xs font-semibold text-slate-500">Einsatz (± Punkte)<Input type="number" min={0} value={f.stake} onChange={set("stake")} data-testid="admin-quest-new-stake" className="rounded-xl mt-1" /></label>
        <label className="text-xs font-semibold text-slate-500">Ende<Input type="datetime-local" value={f.end} onChange={set("end")} data-testid="admin-quest-new-end" className="rounded-xl mt-1" /></label>
      </div>
      <p className="text-xs text-slate-400">0 = Ziel wird nicht verwendet. Gezählt wird ab dem Start. Eine Stunde vor Ende geht automatisch eine Erinnerungs-Push raus, nach dem Ende sehen alle das Ergebnis.</p>
      <button onClick={save} disabled={busy} data-testid="admin-quest-new-save" className="h-10 px-5 rounded-full bg-[#d2042d] text-white text-sm font-bold disabled:opacity-50">Aufgabe starten</button>
    </div>
  );
}
